<script lang="ts" setup>
import { inject, ref, watch, computed, nextTick, onMounted } from 'vue';
import draggable from 'vuedraggable';
import CollapseFieldset from '@/components/CollapseFieldset.vue';
import MenuVerticalDots from '@/assets/images/icons8-menu-vertical.svg?component';
import { HotspotEvents, PerspectiveEvents } from '@/viewerExtensions/events';

import type { Ref } from 'vue';
import type { Viewer, Nullable, Extension } from '@immersive-tech/viewer-engine';
import type { IManifest, IManifestHotspot } from '@/types/IManifest';
import type { Hotspots } from '@/viewerExtensions';

interface IManifestHotspotArrayValue extends IManifestHotspot {
  key: string
}

const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const manifest = inject('manifest') as Ref<IManifest>;
const activeHotspot = inject('activeHotspot') as Ref<Nullable<string>>;
const search = ref('');
const dragging = ref(false);
const hotspotsArray = inject('hotspotsArray') as Ref<IManifestHotspotArrayValue[]>;
const disableDrag = ref(false);
const hotspotsExtension = viewer.value?.extensions.get('Hotspots') as Extension<Hotspots>;

const filteredHotspotsArray = computed(() => {
  const searchValue = search.value.toLowerCase();

  return hotspotsArray.value.filter((hotspot) => hotspot.key.toLowerCase().includes(searchValue));
});

const searchWatcher = (newValue: Nullable<string>) => {
  if (!newValue || newValue.length === 0) {
    disableDrag.value = false;
  } else {
    disableDrag.value = true;
  }
};

const toggleActiveHotspot = (hotspotName: string) => {
  if (activeHotspot.value && !hotspotsExtension.currentLevelHotspots.includes(activeHotspot.value)) {
    hotspotsExtension.hideHotspot(activeHotspot.value);
  }

  if (activeHotspot.value === hotspotName) {
    activeHotspot.value = null;
  } else {
    activeHotspot.value = null;

    nextTick(() => {
      activeHotspot.value = hotspotName;
      hotspotsExtension.showHotspot(hotspotName);
    });
  }
};

const endDrag = () => {
  dragging.value = false;

  const newHotspotArray = hotspotsArray.value.map((hotspot) => hotspot.hotspotName);
  const newManifestHotspots: { [key: string]: IManifestHotspot } = {};

  for (const hotspot of newHotspotArray) {
    newManifestHotspots[hotspot] = manifest.value.hotspots[hotspot];
  }

  manifest.value.hotspots = newManifestHotspots;
};

const startDrag = () => {
  dragging.value = true;
};

const showActiveHotspotHandler = () => {
  if (activeHotspot.value) {
    hotspotsExtension.showHotspot(activeHotspot.value);
  }
};

const resetViewHandler = () => {
  activeHotspot.value = null;
};

const mountHandler = () => {
  viewer.value?.addEventListener(HotspotEvents.HOTSPOT_CLICKED, showActiveHotspotHandler as EventListener);
  viewer.value?.addEventListener(PerspectiveEvents.RESET_VIEW, resetViewHandler);
};

watch(search, searchWatcher);

onMounted(mountHandler);
</script>

<template>
  <CollapseFieldset>
    <template #legend>
      Hotspot List
    </template>

    <input v-model="search" type="text" placeholder="Search..." class="text-xs border w-full p-1 mb-1">

    <template v-if="search.length === 0">
      <div v-if="hotspotsArray.length === 0" class="flex flex-col p-1">
        <span class="text-xs">No hotspots</span>
      </div>

      <draggable
        v-model="hotspotsArray"
        :disabled="disableDrag"
        item-key="order"
        class="max-h-[500px] overflow-y-auto"
        @start="startDrag"
        @end="endDrag"
      >
        <template #item="{element}">
          <div class="flex flex-row space-between items-center p-1" :class="{ 'bg-slate-300': activeHotspot === element.key, 'hover:bg-slate-200/75': activeHotspot !== element.key }">
            <MenuVerticalDots class="cursor-ns-resize" />
            <span class="text-xs flex-1 block cursor-pointer break-word" @click="toggleActiveHotspot(element.key)">{{ element.key }}</span>
          </div>
        </template>
      </draggable>
    </template>

    <template v-if="search.length > 0">
      <div v-if="filteredHotspotsArray.length === 0" class="flex flex-col p-1">
        <span class="text-xs break-word">No hotspots found for search "<span class="font-bold">{{ search }}"</span></span>
      </div>
      <div class="max-h-[500px] overflow-y-auto">
        <div v-for="element in filteredHotspotsArray" :key="element.key" class="flex flex-row space-between items-center p-1" :class="{ 'bg-slate-300': activeHotspot === element.key, 'hover:bg-slate-200/75': activeHotspot !== element.key }">
          <span class="text-xs flex-1 block cursor-pointer" @click="toggleActiveHotspot(element.key)">{{ element.key }}</span>
        </div>
      </div>
    </template>
  </CollapseFieldset>
</template>
